/** The Fold: prestige (GDD §5). */

import { FOLD_MIN_GALAXY_VALUE } from '../content/planets';
import { resource } from '../content/resources';
import { DRIFTS, driftDef } from '../content/drifts';
import { ADDITIONS } from '../content/flags';
import { shuffled } from './rng';
import { fleetStats } from './stats';
import { unitValue } from './economy';
import { roomMult } from './rooms';
import { shipMult } from './ships';
import { newRun, type GameState } from './state';

const DISTILL_GC_PER_TE = 2e6;
/** SC per Fold: a base plus one per planet owned at the Fold. */
const FOLD_SC_BASE = 10;

export function foldSc(state: GameState): number {
  return FOLD_SC_BASE + ownedPlanets(state);
}


export function ownedPlanets(state: GameState): number {
  return state.run.planets.filter((p) => p.owned).length;
}

/** IPM sells a galaxy once its Galaxy Value reaches $10M. */
export function canFold(state: GameState): boolean {
  return !state.run.challenge && state.run.galaxyValue >= FOLD_MIN_GALAXY_VALUE;
}

/**
 * IPM's credit table (wiki: Credit): 10 at $10M, 30 at $100M, 60 at $1B, then
 * ten times each next triangular number per order of magnitude (100, 150,
 * 210, …), linear in value between orders of magnitude.
 */
export function baseCredits(galaxyValue: number): number {
  if (galaxyValue < FOLD_MIN_GALAXY_VALUE) return 0;
  const tri = (n: number) => (n * (n + 1)) / 2;
  const order = Math.floor(Math.log10(galaxyValue) - 7);
  const floorValue = Math.pow(10, 7 + order);
  const frac = (galaxyValue - floorValue) / (floorValue * 9);
  const lo = 10 * tri(order + 1);
  const hi = 10 * tri(order + 2);
  // Rounding (not flooring) reproduces the wiki table: 11 at $12.5M, 12 at $17.5M, 15 at $33M.
  return Math.round(lo + frac * (hi - lo));
}

/** TE (VoidMiner's Credits) for this galaxy, with the Lounge and every TE bonus. */
export function foldTe(state: GameState): number {
  const stats = fleetStats(state);
  const drift = (ADDITIONS.drifts ? driftDef(state.run.drift)?.teMult : undefined) ?? 1;
  return Math.floor(baseCredits(state.run.galaxyValue) * (1 + stats.te) * drift * roomMult(state, 'foldTe') * shipMult(state, 'foldTe'));
}

/** Unfitted alloys and items distil into TE at a poor rate; ores give nothing. */
export function distillTe(state: GameState): number {
  let value = 0;
  for (const [id, qty] of Object.entries(state.run.hold)) {
    if (resource(id).kind !== 'ore') value += unitValue(state, id) * Math.floor(qty);
  }
  return Math.floor(value / DISTILL_GC_PER_TE);
}

/** Rolls the Fold's Drift choices and parks them, so a reload cannot reroll them. */
export function beginFold(state: GameState): boolean {
  if (state.pendingFold) return true;
  if (!canFold(state)) return false;
  state.pendingFold = {
    te: foldTe(state),
    distilled: distillTe(state),
    sc: foldSc(state),
    // With Drifts off the Fold is a plain IPM galaxy sell: no choice to make.
    drifts: ADDITIONS.drifts ? shuffled(state, DRIFTS.map((d) => d.id)).slice(0, 3) : [],
  };
  return true;
}

export function completeFold(state: GameState, driftId: string | null): boolean {
  const offer = state.pendingFold;
  if (!offer || state.run.challenge) return false;
  if (offer.drifts.length > 0 && (!driftId || !offer.drifts.includes(driftId))) return false;
  state.te += offer.te + offer.distilled;
  state.sc += offer.sc;
  state.folds += 1;
  state.run = newRun(offer.drifts.length > 0 ? driftId : null);
  state.pendingFold = null;
  return true;
}
